import React, {useState, useEffect, useRef} from "react";
import axios from "axios";
import {
	base,
	get_contract_instances,
	reports_card,
	contract_related_files,
	contract_download_link,
	partners,
	projects_url,
} from "../../api";

import {useParams, useNavigate} from "react-router-dom";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {Dropdown} from "primereact/dropdown";
import ReportAddDialogForm from "./components/ReportAddDialogForm";
import {DataTable} from "primereact/datatable";
import {Tooltip} from "primereact/tooltip";
import {Toast} from "primereact/toast";
import ReportSingleFileUploader from "./components/ReportSingleFileUploader";
import {
	processProgressBody,
	statusBodyTemplate,
	allowExpansion,
} from "../processes_documentation/widgets/DataTableReuseableWidgets";
import ReportEditDialogForm from "./components/ReportEditDialogForm";

export default function ReportingProcessesItem() {
	const {id: initId} = useParams();
	const [id, setId] = useState(initId);
	const navigate = useNavigate();
	const [data, setData] = useState();
	const [selectedRow, setSelectedRow] = useState();
	const [selectedTemplatePage, setSelectedTemplatePage] = useState(null);
	const [dropdownOptions, setDropdownOptions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [newContractTitle, setNewContractTitle] = useState("new contract title");
	const [isDialogVisible, setIsDialogVisible] = useState(false);
	const [expandedRows, setExpandedRows] = useState(null);
	const [uploadedFile, setUploadedFile] = useState(null);
	const [isFilesDialogVisible, setIsFilesDialogVisible] = useState(false);
	const [isEditDialogVisible, setIsEditDialogVisible] = useState(false);
	const [dialogFileName, setDialogFileName] = useState("");
	const toast = useRef(null);

	useEffect(() => {
		setId(selectedTemplatePage?.slug);
	}, [selectedTemplatePage?.slug]);

	useEffect(() => {
		if (id) {
			fetchData();
			navigate(`/reporting_processes/${id}`, {replace: true});
		}
	}, [id, navigate]);

	const fetchContractData = async () => {
		try {
			const contractResponse = await axios.get(
				`${base + get_contract_instances}/?contract_type=${id}`,
			);

			const sortedData = contractResponse.data.sort(
				(a, b) => a.is_canceled - b.is_canceled || a.value - b.value,
			);

			setData(sortedData);
		} catch (error) {
			setError("Error fetching contract data");
		}
	};

	const fetchPagesData = async () => {
		try {
			const pagesResponse = await axios.get(`${base + reports_card}`);
			setDropdownOptions(pagesResponse.data);
			if (pagesResponse.data && pagesResponse.data.length > 0) {
				setSelectedTemplatePage(pagesResponse.data.find(item => item.slug === id));
			}
		} catch (error) {
			setError("Error fetching pages data");
		}
	};

	const fetchData = async () => {
		try {
			setLoading(true);
			await Promise.all([fetchPagesData(), fetchContractData()]);
		} catch (error) {
			setError("Error fetching data");
		} finally {
			setLoading(false);
		}
	};

	if (loading) return <div>Loading...</div>;
	if (error) return <div>{error}</div>;

	const statusFileUpload = rowData => {
		return (
			<a
				href={base + contract_download_link + rowData.id + "/"}
				icon={"pi pi-download"}
				severity={"success"}
				target="_blank ">
				<Button icon={"pi pi-download"} severity={"success"} text rounded />
			</a>
		);
	};

	const processCancelBody = rowData => {
		return rowData?.is_canceled ? (
			<Button
				icon={"pi pi-times"}
				label="Canceled"
				rounded
				text
				severity="danger"
				className="opacity-75 hover"
				onClick={() => {
					setIsEditDialogVisible(true);
					setSelectedRow(rowData);
				}}
			/>
		) : (
			<Button
				rounded
				text
				disabled
				className="opacity-0 hover"
				onClick={() => {
					setIsEditDialogVisible(true);
					setSelectedRow(rowData);
				}}
			/>
		);
	};

	const processEditBody = rowData => {
		return (
			<Button
				icon={"pi pi-pen-to-square"}
				label="Edit"
				rounded
				text
				className="opacity-50 hover"
				onClick={() => {
					setIsEditDialogVisible(true);
					setSelectedRow(rowData);
				}}
			/>
		);
	};

	const rowExpansionTemplate = rowData => {
		return (
			<div>
				{rowData.contract_type?.file_templates?.map((template, index) => {
					const enabledTemplate = rowData.enabled_template_types?.find(et => et.id === template.id);
					const uploadedFile = enabledTemplate?.uploaded_file;
					const fileNameWithoutPath = template.file_name.split("/").pop();
					const fileNameParts = fileNameWithoutPath.split("_").slice(1);
					const fileDisplayName = fileNameParts.join(" ").replace(/\.\w+$/, "");

					const openDialog = template => {
						if (!enabledTemplate) return;

						setDialogFileName(fileDisplayName);
						setUploadedFile(uploadedFile);
						setIsFilesDialogVisible(true);
						setSelectedRow({...rowData, templateID: template.id});
					};

					return (
						<div
							key={template.id}
							className={`d-flex justify-content-between align-items-center rounded py-1 mb-2 ${
								enabledTemplate ? "text-primary" : "text-muted opacity-50"
							}`}>
							<span className="me-2">{fileDisplayName}</span>

							{uploadedFile ? (
								<div className="me-2">
									<Button
										icon={"pi pi-upload"}
										rounded
										data-pr-position="left"
										className="p-0 px-3 py-1"
										severity="warning"
										id={`download-${template.id}`}
										onClick={() => {
											openDialog(template);
										}}
									/>

									<Tooltip
										target={`#download-${template.id}`}
										content="Download the uploaded process file."
									/>
								</div>
							) : enabledTemplate ? (
								<>
									<div className="col text-end">
										<Button
											icon={"pi pi-upload"}
											rounded
											data-pr-position="left"
											className="p-0 px-3 py-1"
											id={`upload-${template.id}`}
											onClick={() => {
												openDialog(template);
											}}
										/>
									</div>
									<Tooltip
										target={`#upload-${template.id}`}
										content="Upload or replace the process file."
									/>
								</>
							) : (
								<>
									<div className="col text-end">
										<Button
											icon={"pi pi-times"}
											rounded
											data-pr-position="left"
											severity="danger"
											className="p-0 px-3 py-1"
											id={`disabled-${template.id}`}
										/>
									</div>
									<Tooltip
										target={`#disabled-${template.id}`}
										content="The template is not enabled. Please enable the template on the contract creation form."
									/>
								</>
							)}
						</div>
					);
				})}
			</div>
		);
	};

	return (
		<div className="col-12 p-0">
			<div className="card p-3 border-0 b-radius b-shadow">
				<div className="d-flex justify-content-between">
					<div className="col">
						<div className="d-flex align-items-center">
							<div className="">Select process page</div>
							<Dropdown
								value={selectedTemplatePage}
								onChange={e => setSelectedTemplatePage(e.value)}
								options={dropdownOptions}
								optionLabel="name"
								placeholder="Select a Template Page"
								className=" border-0 p-0"
							/>
						</div>
					</div>

					<div className="col-4">
						<div className="d-flex justify-content-end">
							<Button
								label="New contract"
								rounded
								severity="primary"
								onClick={() => setIsDialogVisible(true)}
							/>
						</div>
					</div>
				</div>
				<hr />
				test
				<DataTable
					value={data}
					expandedRows={expandedRows}
					onRowToggle={e => setExpandedRows(e.data)}
					rowExpansionTemplate={rowExpansionTemplate}
					dataKey="id"
					className="text-center"
					tableStyle={{minWidth: "60rem"}}>
					<Column expander={allowExpansion} style={{width: "3rem"}} />
					<Column field="formatted_contract_number" header="ID" sortable className="col-1" />
					<Column field="name" header="Name" sortable className="col-5" />
					<Column header="Edit" body={processEditBody} className="text-start col-1" />
					<Column header="Canceled" body={processCancelBody} className="text-start col-1" />
					<Column header="Processes" className="col-2" body={processProgressBody} />
					<Column
						field="files"
						body={rowData =>
							statusBodyTemplate(rowData, base + contract_download_link + rowData.id + "/")
						}
						className="col-2 text-end"
					/>
				</DataTable>
				<ReportAddDialogForm
					isDialogVisible={isDialogVisible}
					setIsDialogVisible={setIsDialogVisible}
					fetchContractData={fetchContractData}
					setNewContractTitle={setNewContractTitle}
					data={data}
					selectedTemplatePage={selectedTemplatePage}
					combinedURL={base + get_contract_instances}
					toast={toast}
					fetchData={fetchContractData}
					partners={partners}
					base={base}
					projects_url={projects_url}
				/>
				<ReportSingleFileUploader
					dialogHearder={selectedTemplatePage.name + " - " + newContractTitle}
					dialogFileName={dialogFileName}
					uploadedFile={uploadedFile}
					isFilesDialogVisible={isFilesDialogVisible}
					setIsFilesDialogVisible={setIsFilesDialogVisible}
					setUploadedFile={setUploadedFile}
					selectedRow={selectedRow}
					combinedURL={base + contract_related_files}
					toast={toast}
					fetchData={fetchContractData}
				/>
				{/* <ReportEditDialogForm
					isEditDialogVisible={isEditDialogVisible}
					setIsEditDialogVisible={setIsEditDialogVisible}
					selectedRow={selectedRow}
					setSelectedRow={setSelectedRow}
					toast={toast}
					fetchData={fetchContractData}
					partners={partners}
					base={base}
					projects_url={projects_url}
				/> */}
			</div>
			<Toast ref={toast} />
		</div>
	);
}
